/**
 * The state behind **Preferences ▸ Board mapping** (P-294, round 11e).
 *
 * It lives in its own hook rather than in `TasksPage` for one reason: the page is 1,200 lines of
 * list, selection and bulk-bar state that three rounds are editing at once, and a board mapping
 * has nothing to do with any of it. `TasksPage` calls this once and passes the result straight
 * to `TaskSettingsPanel`.
 *
 * **Every change is written immediately**, like the six preferences above it — there is no Save
 * button, and `tasks.mapping.set` is the only writer. The optimistic patch is applied first so
 * the checkbox does not lag behind the finger, and the server's answer replaces it, because the
 * server is the one that decides: changing the status column without naming labels makes it
 * re-detect them, and a `reset` puts the board back on the detector entirely.
 *
 * **A change re-buckets the list**, so `onChanged` re-reads it. That is not cosmetic: the
 * mapping is what `is_done` is derived from, `setTaskMapping` re-derives the whole board in the
 * same transaction (`specs/M10-tasks.md` §3.3), and a page still showing the old buckets would
 * be the P-294 incident's own symptom — a list that disagrees with the board it reads from.
 */
import { useCallback, useEffect, useState } from 'react';
import type { TaskMappingPatch, TaskMappingSectionProps } from './TaskMappingSection';
import type { TaskMappingOptionsView, TaskMappingView, TasksSource } from './types';

export function useTaskMappingPanel(
  source: TasksSource,
  boards: { id: string; name: string }[],
  /** Called after a change lands, so the list re-reads with the new buckets. */
  onChanged: () => void,
  /** `false` while the pane is closed: nothing is read until somebody opens it. */
  active: boolean,
): TaskMappingSectionProps {
  const [boardId, setBoardId] = useState<string | null>(null);
  const [mapping, setMapping] = useState<TaskMappingView | null>(null);
  const [options, setOptions] = useState<TaskMappingOptionsView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* The first board is shown by default; a board that vanishes from the facets is dropped. */
  useEffect(() => {
    if (!active) return;
    if (boardId !== null && boards.some((board) => board.id === boardId)) return;
    setBoardId(boards[0]?.id ?? null);
  }, [active, boards, boardId]);

  useEffect(() => {
    if (!active || boardId === null) return;
    let cancelled = false;
    setBusy(true);
    setError(null);
    /*
     * Both reads together: a mapping without its options cannot be drawn (the done-label list is
     * the status column's, and the column comes from the options), so a half-loaded pane would
     * only be able to show the mapping as raw ids.
     */
    void Promise.all([source.mapping({ boardId }), source.mappingOptions({ boardId })])
      .then(([nextMapping, nextOptions]) => {
        if (cancelled) return;
        setMapping(nextMapping);
        setOptions(nextOptions);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setMapping(null);
        setOptions(null);
        setError(messageOf(cause, 'That board could not be read.'));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, boardId, source]);

  /**
   * Picking the board that is **already** picked must do nothing.
   *
   * It used to clear `mapping` and `options` unconditionally, which looked harmless and was not:
   * `boardId` did not change, so the loader's dependency did not change either, so nothing ever
   * re-read them and the pane sat on "Pick a board to see how it is mapped" forever. A `change`
   * event carrying the current value is not rare — it is what a person re-opening the picker and
   * choosing the same row produces, and what a test harness's `selectOption` produces every
   * time.
   */
  const onSelectBoard = useCallback(
    (next: string) => {
      const id = next === '' ? null : next;
      if (id === boardId) return;
      setBoardId(id);
      setMapping(null);
      setOptions(null);
    },
    [boardId],
  );

  const onChange = useCallback(
    (patch: TaskMappingPatch) => {
      if (boardId === null) return;
      setError(null);
      setBusy(true);
      /* Optimistic, and only for the fields the patch names — never `overridden`, which is the
         server's own answer about who last touched the row. */
      setMapping((current) =>
        current === null || patch.reset === true
          ? current
          : {
              ...current,
              ...(patch.statusColumnId === undefined ? {} : { statusColumnId: patch.statusColumnId }),
              ...(patch.doneLabels === undefined ? {} : { doneLabels: patch.doneLabels }),
              ...(patch.dueColumnId === undefined ? {} : { dueColumnId: patch.dueColumnId }),
              ...(patch.assigneeColumnIds === undefined
                ? {}
                : { assigneeColumnIds: patch.assigneeColumnIds }),
            },
      );
      void source
        .setMapping({ boardId, ...patch })
        .then((next) => {
          setMapping(next);
          onChanged();
        })
        .catch((cause: unknown) => {
          setError(messageOf(cause, 'That change was refused.'));
          /* Put the row back the way the server has it, rather than leaving the optimistic
             patch on screen claiming a change that did not happen. */
          return source
            .mapping({ boardId })
            .then(setMapping)
            .catch(() => undefined);
        })
        .finally(() => setBusy(false));
    },
    [boardId, source, onChanged],
  );

  return { boards, boardId, mapping, options, busy, error, onSelectBoard, onChange };
}

/** The server's own sentence, or a fallback. Never a stack, never an empty toast. */
function messageOf(cause: unknown, fallback: string): string {
  const message = cause instanceof Error ? cause.message : typeof cause === 'string' ? cause : '';
  const first = message.split('\n')[0]?.trim() ?? '';
  if (first === '') return fallback;
  return first.length > 200 ? `${first.slice(0, 197)}…` : first;
}
